import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { ChipStatus, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { dateDiff, formatDateTime } from '../../../shared/utils';
import { ListId, LocalStorange } from '../../../shared/environment';
import { TestPackageDTO, UserDTO, ValueListDTO, ValueListFilterDTO, WorkspaceDTO } from '../../../shared/interfaces';
import { WorkspaceFilterDTO } from '../../../shared/interfaces/testingSphere/WorkspaceFilterDTO';

interface ITestPackageMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: true) => void;
    parentId?: string;
}

export const TestPackageMaintenance: React.FC<ITestPackageMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted, parentId }) => {
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [executionStatusList, setExecutionStatusList] = useState<ValueListDTO[]>([]);
    const [testLevelList, setTestLevelList] = useState<ValueListDTO[]>([]);
    const [workspaceList, setWorkspaceList] = useState<WorkspaceDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [testPackageDTO, setTestPackageDTO] = useState<TestPackageDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setTestPackageDTO({
            ...testPackageDTO,
            id: '',
            name: '',
            testLevelId: '',
            workspaceId: parentId ?? '',
            orderExecution: 0,
            description: '',
            statusId: ListId.testingsphere.testpackage.statusidstart,
            executionStatusId: ListId.testingsphere.testpackage.executionstatusstart
        } as TestPackageDTO);

        if(isOpenMaintenance){
            handleWorkspaceGetAllAsync();
            handleListGetAll(ListId.testingsphere.testpackage.executionstatus, setExecutionStatusList);
            handleListGetAll(ListId.testingsphere.testpackage.testlevel, setTestLevelList);
            handleListGetAll(ListId.testingsphere.testpackage.statusId, setStatusList);
            handleUserGetAllAsync();
            handleGetByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleWorkspaceGetAllAsync = useCallback(async () => {
        const workspaceFilterDTO: WorkspaceFilterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('testingsphere.workspace', workspaceFilterDTO, setLoading, setWorkspaceList, showMessage);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleUserGetAllAsync = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleGetByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('testingsphere.testPackage', data.id, setLoading, setTestPackageDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {

        const dto = testPackageDTO as TestPackageDTO;

        if (testPackageDTO?.id === '' || testPackageDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('testingsphere.testPackage', dto, setTestPackageDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('testingsphere.testPackage', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [testPackageDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='TestPackage Details'
                icon='integration_instructions_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={testPackageDTO?.name}
                                setValue={setTestPackageDTO}
                                obj={testPackageDTO}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='testLevelId'
                                label={'Test Level'}
                                value={testPackageDTO?.testLevelId}
                                setValue={setTestPackageDTO}
                                obj={testPackageDTO}
                                valueList={testLevelList}
                            />
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <SelectField
                                id='workspaceId'
                                label={'Workspace'}
                                value={testPackageDTO?.workspaceId}
                                setValue={setTestPackageDTO}
                                obj={testPackageDTO}
                                valueList={workspaceList}
                                disabled={parentId ?? testPackageDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='orderExecution'
                                label='Order Execution'
                                value={testPackageDTO?.orderExecution}
                                setValue={setTestPackageDTO}
                                obj={testPackageDTO}
                                maxLength={4}
                                type={'number'}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={testPackageDTO?.description}
                                setValue={setTestPackageDTO}
                                obj={testPackageDTO}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldReadOnly
                                id={'executionStatusId'}
                                label='Status Execution'
                                value={(<ChipStatus label='Execution Status' value={String(executionStatusList.find(x => x.id === testPackageDTO?.executionStatusId)?.value || 'No Run')} />)}
                                hasDivider={false}
                                hasToolTip={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'statusId'}
                                label='Enable to Run'
                                value={(<ChipStatus label='Enable to Run' value={String(statusList.find(x => x.id === testPackageDTO?.statusId)?.value || 'Unavailable')} />)}
                                hasDivider={false}
                                hasToolTip={true}
                            />
                        </Grid>
                        {testPackageDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'startExecutionDateTime'}
                                        label='Start Execution'
                                        value={formatDateTime(String(testPackageDTO.startExecutionDateTime))}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'endExecutionDateTime'}
                                        label='End Execution'
                                        value={formatDateTime(String(testPackageDTO.endExecutionDateTime))}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'executionTime'}
                                        label='Execution Time'
                                        value={testPackageDTO.startExecutionDateTime ? dateDiff(String(testPackageDTO.startExecutionDateTime), String(testPackageDTO.endExecutionDateTime)) : undefined}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={testPackageDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === testPackageDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(testPackageDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === testPackageDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(testPackageDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};